/**
 * WhisperLoadingModal — full-screen loading state shown when the user
 * tries to start a session before the Whisper model finishes fetching.
 *
 * The model is ~145 MB on first launch and downloaded from Hugging Face
 * CDN via Transformers.js, then cached by the Cache Storage API. On
 * subsequent launches the cache hit is near-instant and this modal
 * typically flashes briefly or not at all.
 *
 * Failure UX: if loading errors out (CDN down, browser refuses storage,
 * etc.), the modal switches to an error state and offers a "Continue
 * without transcription" escape. Per PRD §10.2 the user can still play
 * — recording works, transcripts are empty, the grade pipeline runs on
 * whatever it received.
 */

import { useEffect, useState } from 'react';
import {
  whisperAdapter,
  type WhisperLoadStatus,
} from '@/services/whisperAdapter';
import { cn } from '@/lib/utils';

interface WhisperLoadingModalProps {
  /** Resolve and proceed once the model is ready. */
  onReady: () => void;
  /** User chose to continue without transcription. */
  onContinueWithoutTranscription: () => void;
}

export function WhisperLoadingModal({
  onReady,
  onContinueWithoutTranscription,
}: WhisperLoadingModalProps) {
  const [status, setStatus] = useState<WhisperLoadStatus>(
    whisperAdapter.getStatus(),
  );

  useEffect(() => {
    const unsub = whisperAdapter.subscribe(setStatus);
    // Also kick the load if for some reason it isn't already in flight
    // (e.g. user opened SessionConfig directly via deep link, skipping
    // the Consent screen's ensureLoaded call).
    void whisperAdapter.ensureLoaded().catch(() => {
      /* surfaced via subscribed status */
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (status.phase === 'ready') {
      onReady();
    }
  }, [status, onReady]);

  const pct =
    status.phase === 'loading' ? Math.round(status.progress * 100) : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whisper-modal-title"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-elevated p-6 shadow-2xl">
        <h2
          id="whisper-modal-title"
          className="mb-2 text-lg font-bold text-text-primary"
        >
          {status.phase === 'error'
            ? 'Transcription unavailable'
            : 'Preparing transcription model'}
        </h2>

        {status.phase !== 'error' && (
          <>
            <p className="mb-4 text-sm text-text-secondary">
              First-time setup. The model (~145 MB) is downloading and
              caching to your device. Future sessions will start instantly.
            </p>
            <div
              className="mb-2 h-2 overflow-hidden rounded-full bg-surface-app"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full bg-brand-accent transition-[width] duration-300 ease-linear"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-right text-xs font-mono tabular-nums text-text-muted">
              {pct}%
            </div>
          </>
        )}

        {status.phase === 'error' && (
          <p className="mb-4 text-sm text-text-secondary">
            The transcription model couldn't be loaded. You can still play
            and your decisions will be recorded, but transcripts will be
            empty and grade detail may be limited.
          </p>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onContinueWithoutTranscription}
            className={cn(
              'rounded-md border border-border bg-surface-app px-4 py-2 text-sm font-semibold',
              'text-text-secondary hover:text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
            )}
          >
            {status.phase === 'error'
              ? 'Continue anyway'
              : 'Continue without transcription'}
          </button>
        </div>
      </div>
    </div>
  );
}
